import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from '@material-ui/core';
import { State, WorkflowInvoke } from '@sierralabs/workflow';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin,
  TaskContext
} from '@sierralabs/workflow-ui';

import TestRailApiProjectPicker from '../../../test-rail/components/TestRailApiProjectPicker';
import TestRailApiSectionPicker from '../../../test-rail/components/TestRailApiSectionPicker';
import TestRailApiSuitePicker from '../../../test-rail/components/TestRailApiSuitePicker';
import {
  TestRailApiProject,
  TestRailApiSection,
  TestRailApiSuite
} from '../../../test-rail/TestRail.interface';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  a {
    text-decoration: underline;
  }
`;

export interface CreateTestRailCaseParams {
  src: 'createTestRailCase';
  data: {
    project?: TestRailApiProject;
    suite?: TestRailApiSuite;
    section?: TestRailApiSection;
  };
}

const CreateTestRailCaseConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { onChange } = props;
  const [invoke, setInvoke] = useState(props.invoke);
  if (!invoke.data) invoke.data = {};
  const data: CreateTestRailCaseParams['data'] = invoke.data;

  const update = (data: CreateTestRailCaseParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  return (
    <StyledConfiguratorDiv>
      <TestRailApiProjectPicker
        className='field'
        project={data.project}
        textFieldProps={{ size: 'small' }}
        onChange={value => {
          update({
            ...data,
            project: value || undefined,
            suite: undefined,
            section: undefined
          });
        }}
      />
      <TestRailApiSuitePicker
        className='field'
        suite={data.suite}
        testRailProjectId={data.project?.id}
        disabled={!data.project}
        textFieldProps={{ size: 'small' }}
        onChange={value => {
          update({ ...data, suite: value || undefined, section: undefined });
        }}
      />
      <TestRailApiSectionPicker
        className='field'
        section={data.section}
        testRailProjectId={data.project?.id}
        testRailSuiteId={data.suite?.id}
        disabled={!data.project || !data.suite}
        textFieldProps={{
          size: 'small',
          helperText: data.section ? undefined : (
            <>
              A section is required to create the test case. Please make sure
              that at least one section is available in your{' '}
              {data.suite ? (
                <Link target='_blank' href={data.suite.url}>
                  TestRail suite
                </Link>
              ) : (
                'TestRail suite'
              )}{' '}
              to proceed.
            </>
          )
        }}
        onChange={value => update({ ...data, section: value || undefined })}
      />
    </StyledConfiguratorDiv>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
  padding-left: 4px;
`;

const CreateTestRailCaseDisplay: React.FC<ServiceDisplayProps> = props => {
  const { invoke } = props;
  const data = invoke?.data as CreateTestRailCaseParams['data'] | undefined;
  return (
    <StyledDisplayDiv>
      {data?.project && (
        <div>
          <strong>Project</strong>: P{data.project.id}: {data.project.name}
        </div>
      )}
      {data?.suite && (
        <div>
          <strong>Suite</strong>: S{data.suite.id}: {data.suite.name}
        </div>
      )}
      {data?.section && (
        <div>
          <strong>Section</strong>: {data.section.name}
        </div>
      )}
    </StyledDisplayDiv>
  );
};

export const CreateTestRailCasePlugin: ServicePlugin<CreateTestRailCaseParams> =
  {
    src: 'createTestRailCase',
    name: 'Create TestRail Test Case',
    configurator: CreateTestRailCaseConfigurator,
    display: CreateTestRailCaseDisplay,
    validate: (condition: Partial<WorkflowInvoke>) => {
      const data = condition.data as CreateTestRailCaseParams['data'];
      return !!(data && data.project && data.suite && data.section);
    },
    service: (state: State<TaskContext>, params: CreateTestRailCaseParams) => {
      console.log('createTestRailCase params', state, params);
      return true;
    }
  };
